import { Fragment, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, InfoIcon } from '@/components/icons';
import { cn } from '@/utils/cn';

export interface TranslatedSpec {
  /** Lookup key under `products.specLabels.*`, also used as React key. */
  labelKey: string;
  /** Already-translated value rendered in the spec cell. */
  value: string;
}

interface SpecPanelProps {
  /** Heading shown next to the info icon (e.g. "Lens specifications"). */
  label: string;
  specs: readonly TranslatedSpec[];
  /**
   * Cells per row at xl+:
   *   3 — lens spec panels
   *   2 — Eyedia solution spec panel
   */
  columns?: 2 | 3;
  /**
   * Optional CTA rendered at the last row's last column (e.g. "View color
   * range" for the Color family). Each row uses a CSS grid so cells stay
   * locked to their column even when the row has fewer specs than columns.
   */
  cta?: ReactNode;
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/**
 * Spec panel — collapsible bordered card with expand/collapse animation.
 *
 * States:
 *   Collapsed (default): just the header row (info icon + label + chevron-down).
 *     Hover paints `bg-clearlab-divider-light/20` (same fill as outlined-button
 *     hover) to advertise the click-to-expand affordance.
 *   Expanded: header row (chevron rotates 180° → points up) + animated content
 *     reveal — sections of label/value cells separated by 0.7px hairlines.
 *
 * Interaction:
 *   - Whole card is the click target. Clicking the collapsed card expands;
 *     clicking the expanded card collapses.
 *   - Hover state only when collapsed (per design — collapsing has no hover).
 *
 * Animation (snappy ease-in, 120ms via `duration-fast`):
 *   - Height grows via the grid-template-rows 0fr → 1fr trick (avoids fixed
 *     max-height guesswork and animates to natural content height).
 *   - Content fades in 0 → 100% opacity in parallel.
 */
export function SpecPanel({ label, specs, columns = 3, cta }: SpecPanelProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const rows = chunk(specs, columns);
  return (
    <button
      type="button"
      onClick={() => setExpanded((e) => !e)}
      aria-expanded={expanded}
      className={cn(
        'block w-full rounded-lg border border-clearlab-divider-darker p-4 text-left',
        'transition-colors duration-fast ease-in',
        !expanded && 'hover:bg-clearlab-divider-light/20',
      )}
    >
      <div className="flex w-full items-center gap-2">
        <InfoIcon className="size-4 shrink-0 text-clearlab-midnight" />
        <span className="flex-1 text-[16px] font-normal leading-[18px] text-clearlab-midnight">
          {label}
        </span>
        <ChevronDownIcon
          className={cn(
            'size-4 shrink-0 text-clearlab-midnight transition-transform duration-fast ease-in',
            expanded && 'rotate-180',
          )}
        />
      </div>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-fast ease-in',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-4 pt-4 transition-opacity duration-fast ease-in',
              expanded ? 'opacity-100' : 'opacity-0',
            )}
          >
            <hr className="w-full border-0 border-t-[0.7px] border-clearlab-divider-darker" />

            {rows.map((row, idx) => {
              const isLastRow = idx === rows.length - 1;
              return (
                <Fragment key={idx}>
                  {idx > 0 ? (
                    <hr className="w-full border-0 border-t-[0.7px] border-clearlab-divider-darker" />
                  ) : null}
                  <div
                    className={cn(
                      'grid w-full items-start gap-2',
                      columns === 3 ? 'grid-cols-3' : 'grid-cols-2',
                    )}
                  >
                    {/* `display: contents` so the <dl> keeps its list semantics
                        while the label/value cells become direct grid items.
                        Each cell is a div wrapping a single dt/dd pair (valid
                        <dl> content); the CTA lives outside the <dl>. */}
                    <dl className="contents">
                      {row.map((spec) => (
                        <div
                          key={spec.labelKey}
                          className="flex min-w-0 flex-col items-start justify-center"
                        >
                          <dt className="w-full text-[14px] font-normal leading-5 text-clearlab-steel">
                            {t(`products.specLabels.${spec.labelKey}`)}
                          </dt>
                          <dd className="w-full whitespace-pre-line text-[14px] font-normal leading-5 text-clearlab-midnight">
                            {spec.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    {isLastRow && cta ? <div className="col-start-2 min-w-0">{cta}</div> : null}
                  </div>
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </button>
  );
}
